import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VerifiedIcon from "@mui/icons-material/Verified";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

const BUYER_TYPES = [
  "Entreprise / Centrale d'achat / Grossiste",
  "Supermarché / Épicerie",
  "Hôtel / Restaurant / Traiteur",
  "Particulier / Famille",
  "Industrie Agroalimentaire"
];

const GRAND_LIBREVILLE_COMMUNES = [
  "Libreville",
  "Akanda",
  "Owendo"
];

const AVG_WHATSAPP_NUMBER = "24104126488"; // Numéro officiel WhatsApp Agri Vision Gabon (+241 04 12 64 88)

const MarketplaceOrderModal = ({ open, lot, onClose }) => {
  const theme = useTheme();

  const [buyerType, setBuyerType] = useState("Entreprise / Centrale d'achat / Grossiste");
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("Libreville");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [quantityUnit, setQuantityUnit] = useState("kg"); // "kg" ou "tonnes"
  const [quantity, setQuantity] = useState(lot ? (lot.minOrderKg >= 1000 ? "1" : "50") : "50");
  const [desiredDate, setDesiredDate] = useState("");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  if (!lot) return null;

  const numericQuantity = parseFloat(quantity) || 0;
  const quantityInKg = quantityUnit === "tonnes" ? numericQuantity * 1000 : numericQuantity;
  const totalPriceFcfa = Math.round(quantityInKg * lot.pricePerKg);

  const resetForm = () => {
    setSuccessOrder(null);
    setErrorMessage(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Construire le message WhatsApp pré-rempli
  const buildWhatsAppMessage = (orderRef = null) => {
    const isPreorder = lot.status === "preorder";
    const refText = orderRef ? ` (Réf: ${orderRef})` : "";
    
    return encodeURIComponent(
      `Bonjour Agri Vision Gabon,\n\n` +
      `Je souhaite passer une ${isPreorder ? "PRÉCOMMANDE" : "COMMANDE"}${refText} sur la Centrale d'Achat Agricole :\n\n` +
      `🌱 *Produit :* ${lot.product} - ${lot.variety}\n` +
      `🚚 *Livraison garantie :* Grand Libreville (Akanda, Owendo, Libreville)\n` +
      `📦 *Quantité :* ${quantity} ${quantityUnit} (~${totalPriceFcfa.toLocaleString()} FCFA)\n` +
      (isPreorder ? `⏳ *Date de récolte prévue :* ${lot.harvestDate || "À venir"}\n` : `🚚 *Disponibilité :* Stock immédiat (livré sous 24h)\n`) +
      `\n*Coordonnées de livraison :*\n` +
      `👤 *Client :* ${customerName || "Non spécifié"}\n` +
      `🏢 *Profil :* ${buyerType}\n` +
      `📞 *Téléphone :* ${phone || "Non spécifié"}\n` +
      `🏙️ *Ville/Lieu :* ${city} - ${deliveryAddress}\n` +
      (desiredDate ? `📅 *Date souhaitée :* ${desiredDate}\n` : "") +
      (notes ? `💬 *Note :* ${notes}\n` : "") +
      `\nMerci de me confirmer la disponibilité et les modalités de livraison.`
    );
  };

  const handleWhatsAppDirect = () => {
    const message = buildWhatsAppMessage();
    const url = `https://wa.me/${AVG_WHATSAPP_NUMBER}?text=${message}`;
    window.open(url, "_blank");
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    if (!customerName.trim() || !phone.trim()) {
      setErrorMessage("Veuillez renseigner votre nom et votre numéro de téléphone.");
      return;
    }

    if (quantityInKg < (lot.minOrderKg || 1)) {
      setErrorMessage(`La quantité minimale de commande pour ce lot est de ${lot.minOrderKg} kg.`);
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    const orderId = `AVG-CMD-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 90000)}`;

    try {
      // Enregistrement dans Firestore si disponible
      if (db) {
        await addDoc(collection(db, "marketplace_orders"), {
          orderId,
          lotId: lot.id,
          product: lot.product,
          variety: lot.variety,
          province: lot.province,
          cooperative: lot.cooperative,
          status: lot.status === "preorder" ? "precommande_en_attente" : "commande_en_attente",
          quantityInput: numericQuantity,
          quantityUnit,
          quantityInKg,
          pricePerKg: lot.pricePerKg,
          totalPriceEstimated: totalPriceFcfa,
          harvestDate: lot.harvestDate,
          customerName: customerName.trim(),
          buyerType,
          phone: phone.trim(),
          city,
          deliveryAddress: deliveryAddress.trim(),
          desiredDeliveryDate: desiredDate,
          notes: notes.trim(),
          source: "centrale_achat_map",
          createdAt: serverTimestamp()
        });
      }

      setSuccessOrder({
        orderId,
        lot,
        quantity,
        quantityUnit,
        totalPriceFcfa,
        customerName,
        phone,
        city
      });
    } catch (err) {
      console.warn("Erreur Firestore (mode déconnecté actif) :", err);
      // Succès local quand même pour une expérience sans friction
      setSuccessOrder({
        orderId,
        lot,
        quantity,
        quantityUnit,
        totalPriceFcfa,
        customerName,
        phone,
        city
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: { xs: 1, sm: 2 },
          maxHeight: "92vh"
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingBagIcon sx={{ color: theme.palette.primary.main, fontSize: "1.8rem" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1.2 }}>
              {lot.status === "preorder" ? "Précommander cette Récolte" : "Commander sur la Centrale d'Achat"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Centrale d'Achat Agricole Agri Vision Gabon • Réf. {lot.id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {successOrder ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: "#28a745", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
              {lot.status === "preorder" ? "Précommande enregistrée avec succès !" : "Commande transmise avec succès !"}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "text.secondary", mb: 3 }}>
              Numéro de bon : <strong>{successOrder.orderId}</strong>
            </Typography>

            <Alert severity="success" sx={{ mb: 3, textAlign: "left", borderRadius: "10px" }}>
              <Typography variant="body2">
                Votre demande de <strong>{successOrder.quantity} {successOrder.quantityUnit}</strong> de{" "}
                <strong>{lot.product}</strong> est enregistrée pour une livraison dans le <strong>Grand Libreville ({successOrder.city})</strong>.
                Un conseiller logistique Agri Vision Gabon vous contactera sous 2 heures pour organiser la remise.
              </Typography>
            </Alert>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  const msg = buildWhatsAppMessage(successOrder.orderId);
                  window.open(`https://wa.me/${AVG_WHATSAPP_NUMBER}?text=${msg}`, "_blank");
                }}
                sx={{
                  backgroundColor: "#25D366",
                  color: "#fff",
                  fontWeight: 600,
                  py: 1.2,
                  px: 3,
                  "&:hover": { backgroundColor: "#1ebc59" }
                }}
              >
                Confirmer sur WhatsApp Direct
              </Button>
              <Button variant="outlined" onClick={handleClose} sx={{ py: 1.2, px: 3 }}>
                Fermer
              </Button>
            </Box>
          </Box>
        ) : (
          <form onSubmit={handleSubmitOrder}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "8px" }}>
                {errorMessage}
              </Alert>
            )}

            {/* Récapitulatif du lot */}
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {lot.product}
                    </Typography>
                    <Chip
                      size="small"
                      label={lot.status === "in_stock" ? "En stock immédiat" : `Récolte dans ${lot.daysToHarvest}j`}
                      sx={{
                        backgroundColor: lot.status === "in_stock" ? "#28a745" : "#ff9800",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                    Variété : <strong>{lot.variety}</strong> • Catégorie : {lot.category}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "#014527" }}>
                    <LocalShippingIcon sx={{ fontSize: 16, color: "#059669" }} />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Livraison garantie : Grand Libreville (Akanda, Owendo, Libreville)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    Prix indicatif
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {lot.pricePerKg.toLocaleString()} FCFA
                    <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                      {" "}
                      / kg
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    (~{lot.pricePerTonne.toLocaleString()} FCFA / t)
                  </Typography>
                </Grid>
              </Grid>

              {lot.status === "preorder" && (
                <Box
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <CalendarMonthIcon fontSize="small" sx={{ color: "#e65100" }} />
                  <Typography variant="caption" sx={{ color: "#e65100", fontWeight: 600 }}>
                    Cycle cultural AVG Farm : Récolte prévisionnelle le {lot.harvestDate} (volume réservable : {lot.availableTonnes} tonnes).
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Champs de configuration de la commande */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.palette.primary.main }}>
              1. Volume & Quantité souhaitée
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={7} sm={8}>
                <TextField
                  fullWidth
                  label="Quantité demandée"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1, step: quantityUnit === "tonnes" ? "0.1" : "1" }}
                  helperText={`Minimum : ${lot.minOrderKg} kg • Stock total : ${lot.availableTonnes} t`}
                  size="small"
                />
              </Grid>
              <Grid item xs={5} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Unité"
                  value={quantityUnit}
                  onChange={(e) => setQuantityUnit(e.target.value)}
                  size="small"
                >
                  <MenuItem value="kg">Kilogrammes (kg)</MenuItem>
                  <MenuItem value="tonnes">Tonnes (t)</MenuItem>
                </TextField>
              </Grid>

              {/* Raccourcis de quantité rapide */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Sélection rapide :
                  </Typography>
                  {["50", "100", "250", "500"].map((q) => (
                    <Chip
                      key={q}
                      label={`${q} kg`}
                      size="small"
                      clickable
                      onClick={() => {
                        setQuantityUnit("kg");
                        setQuantity(q);
                      }}
                      variant={quantityUnit === "kg" && quantity === q ? "filled" : "outlined"}
                      color="primary"
                    />
                  ))}
                  {["1", "2", "5", "10"].map((q) => (
                    <Chip
                      key={q}
                      label={`${q} t (Gros B2B)`}
                      size="small"
                      clickable
                      onClick={() => {
                        setQuantityUnit("tonnes");
                        setQuantity(q);
                      }}
                      variant={quantityUnit === "tonnes" && quantity === q ? "filled" : "outlined"}
                      color="secondary"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Estimation financière */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: alpha("#28a745", 0.08),
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e7e34" }}>
                    Estimation indicative de la commande :
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e7e34" }}>
                    {totalPriceFcfa.toLocaleString()} FCFA
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Coordonnées de l'acheteur */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: theme.palette.primary.main }}>
              2. Informations Acheteur & Livraison
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Profil acheteur"
                  value={buyerType}
                  onChange={(e) => setBuyerType(e.target.value)}
                  size="small"
                >
                  {BUYER_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Nom complet / Raison sociale"
                  placeholder="Ex: Supermarché Gabon Prix ou M. MBA"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Téléphone / WhatsApp"
                  placeholder="Ex: 066 12 34 56 ou +241 ..."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Commune de livraison (Grand Libreville)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  size="small"
                >
                  {GRAND_LIBREVILLE_COMMUNES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Adresse de livraison / Point de collecte"
                  placeholder="Ex: PK8 Libreville, Zone Industrielle d'Oloumi..."
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de livraison souhaitée"
                  type="date"
                  value={desiredDate}
                  onChange={(e) => setDesiredDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarques particulières (calibrage, conditionnement en sacs, conteneur...)"
                  placeholder="Précisez ici vos exigences de calibrage ou de livraison..."
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>

            {/* Badges de confiance */}
            <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                <VerifiedIcon fontSize="small" color="primary" />
                <Typography variant="caption">Origine 100% Gabon certifiée</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                <LocalShippingIcon fontSize="small" color="primary" />
                <Typography variant="caption">Logistique centralisée AVG</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                <CheckCircleIcon fontSize="small" color="primary" />
                <Typography variant="caption">Paiement sécurisé à la livraison / Virement B2B</Typography>
              </Box>
            </Box>

            <DialogActions sx={{ px: 0, pt: 3, pb: 0, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={handleWhatsAppDirect}
                startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  borderColor: "#25D366",
                  color: "#128C7E",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#128C7E", backgroundColor: alpha("#25D366", 0.05) }
                }}
              >
                Commander via WhatsApp Direct
              </Button>

              <Box sx={{ flex: 1 }} />

              <Button onClick={handleClose} color="inherit" sx={{ width: { xs: "100%", sm: "auto" } }}>
                Annuler
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ShoppingBagIcon />}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  "&:hover": { backgroundColor: "#01341d" }
                }}
              >
                {lot.status === "preorder" ? "Valider la précommande" : "Valider la commande"}
              </Button>
            </DialogActions>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MarketplaceOrderModal;
